import React, { useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import Styles from './TokensActivityCard.module.css'
import { Colors } from 'Theme'
import PropTypes from 'prop-types'
import { ChartComponent, IFSkeleton, IFText } from 'Components'
import AnalyticsSelectors from 'Stores/Analytics/Selectors'
import AnalyticsActions from 'Stores/Analytics/Actions'
import RequestState from 'Enums/RequestState'
import { formatNumber } from 'Utils/Calculations'
import { useTranslation } from 'react-i18next'
import InfinityEnums from 'Enums/InfinityEnums'

const TokensActivityCard = ({
  tokensActivity,
  fetchTokensActivity,
  fetchTokensActivityRequestState,
}) => {
  const { t } = useTranslation()

  useEffect(() => {
    fetchTokensActivity()
  }, [])
  const { series, options } = useMemo(() => {
    const data = tokensActivity
    const periodDisplay = {
      lastYear: t('Analytics.LastYear'),
      thisYear: t('Analytics.ThisYear'),
      last6Months: t('Analytics.Last6Months'),
      last3Months: t('Analytics.Last3Months'),
      lastMonth: t('Analytics.LastMonth'),
      lastWeek: t('Analytics.LastWeek'),
    }

    const totalTokens = data?.periodData?.thisYear?.data?.totalTokens || 0

    const activeTokensPerPeriod = Object.keys(
      InfinityEnums.TokensActivityDurations,
    ).map((key) => {
      return data?.periodData?.[key]?.data?.activeTokens || 0
    })

    const seriesData = activeTokensPerPeriod.map((active) =>
      totalTokens > 0 ? (active / totalTokens) * 100 : 0,
    )

    const labels = Object.keys(InfinityEnums.TokensActivityDurations).map(
      (key, idx) => {
        const count = activeTokensPerPeriod[idx] || 0
        return `${formatNumber(count)}`
      },
    )

    return {
      series: seriesData,

      options: {
        chart: {
          height: 390,
          type: 'radialBar',
        },
        plotOptions: {
          radialBar: {
            offsetY: 0,
            startAngle: 0,
            endAngle: 270,
            hollow: {
              margin: 5,
              size: '30%',
              background: 'transparent',
              image: undefined,
            },
            dataLabels: {
              name: {
                show: false,
              },
              value: {
                show: true,
                fontSize: '16px',
                fontWeight: 600,
                formatter: function (val) {
                  const rawValue = Math.round((val / 100) * totalTokens)
                  return formatNumber(rawValue)
                },
              },
              total: {
                show: true,
                label: t('Analytics.Total'),
                formatter: () => formatNumber(totalTokens),
              },
            },
            barLabels: {
              enabled: true,
              useSeriesColors: true,
              offsetX: -18,
              fontSize: '12px',
              formatter: function (seriesName, opts) {
                const idx = opts.seriesIndex
                const periodKeys = Object.keys(
                  InfinityEnums.TokensActivityDurations,
                )
                const key = periodKeys[idx]
                return `${periodDisplay[key]}`
              },
            },
          },
          total: {
            show: true,
            label: formatNumber(totalTokens),
            formatter: () => '',
          },
        },
        labels,
        colors: [
          Colors.Analytics.RadialBar.LastYear,
          Colors.Analytics.RadialBar.ThisYear,
          Colors.Analytics.RadialBar.Last6Months,
          Colors.Analytics.RadialBar.Last3Months,
          Colors.Analytics.RadialBar.LastMonth,
          Colors.Analytics.RadialBar.LastWeek,
        ],

        fill: {
          type: 'solid',
          opacity: 1,
        },
        stroke: {
          lineCap: 'round',
          width: 2,
        },
      },
    }
  }, [tokensActivity, t])

  return (
    <div style={{ backgroundColor: Colors.white }} className={Styles.Card}>
      {fetchTokensActivityRequestState === RequestState.LOADING ? (
        <div className={Styles.ChartSkeleton}>
          <IFSkeleton width={'100%'} height={'100%'} variant="rectangular" />
        </div>
      ) : (
        <div className={Styles.Content}>
          <ChartComponent
            type="radialBar"
            options={options}
            series={series}
            height={'100%'}
            width={'100%'}
            className={Styles.Chart}
          />
        </div>
      )}
    </div>
  )
}

const mapStateToProps = (state) => ({
  tokensActivity: AnalyticsSelectors.getTokensActivity(state),
  fetchTokensActivityRequestState:
    AnalyticsSelectors.getTokensActivityRequestState(state),
})

function mapDispatchToProps(dispatch) {
  return {
    fetchTokensActivity: () => dispatch(AnalyticsActions.fetchTokensActivity()),
  }
}

TokensActivityCard.propTypes = {
  fetchTokensActivity: PropTypes.func,
  fetchTokensActivityRequestState: PropTypes.number,
  tokensActivity: PropTypes.object,
}

export default connect(mapStateToProps, mapDispatchToProps)(TokensActivityCard)
